import { useQuery } from '@tanstack/react-query';
import { Box, Flex, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faHashtag } from '@fortawesome/pro-regular-svg-icons';
import { ButtonText, OutlineCard, Table } from '@morressier/alembic';

import { Overlay } from 'src/components/Overlay';
import { EmailRecipientRow } from 'src/components/Submission/ActivityTab/EmailViewModal';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import SubmissionLoader from 'src/pages/Organization/Workspace/Submissions/SubmissionLoader';

import { removeHrefKeepText, renderRecipient } from 'src/utils/email.utils';
import { formatDateAndTime } from 'src/utils/date';
import {
  fetchEmailTrackerData,
  IEmailHistoryItem,
  IWebhookEvent
} from 'src/api/queries';

const getDisplayLabel = (value?: string) => {
  if (!value) return 'NA';
  const label = value
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const getEventLabel = (eventType: string) => getDisplayLabel(eventType);

const TextWithIcon = ({ icon, text }: { icon: any; text: string }) => (
  <Flex alignItems="center" gap={2}>
    <FontAwesomeIcon icon={icon} color="var(--chakra-colors-iconSubtle)" />
    <Text textStyle="textBody">{text}</Text>
  </Flex>
);

const TrackerDeliveryDetailsTable = ({ logs }: { logs: IWebhookEvent[] }) => (
  <Box width="100%" overflowX="auto">
    <Box minWidth="860px">
      <Table>
        <Table.HeaderRow gap={4}>
          <Table.HeaderCell flex="1.2">Event Type</Table.HeaderCell>
          <Table.HeaderCell flex="1">Reason</Table.HeaderCell>
          <Table.HeaderCell flex="1.4">Event Time</Table.HeaderCell>
        </Table.HeaderRow>
        <Table.Body>
          {logs.map((item, index) => (
            <Table.Row
              key={item._id ?? `webhook-row-${index}`}
              gap={4}
              _last={{ borderBottom: 'none' }}
            >
              <Table.RowCell flex="1.2" paddingY={3}>
                <Text textStyle="textBody">
                  {getEventLabel(item.eventType)}
                </Text>
              </Table.RowCell>
              <Table.RowCell flex="1" paddingY={3} minWidth="0" overflow="hidden">
                <Text
                  textStyle="textBody"
                  whiteSpace="normal"
                  wordBreak="break-word"
                  overflowWrap="anywhere"
                >
                  {String(item.eventData?.reason || '--')}
                </Text>
              </Table.RowCell>
              <Table.RowCell flex="1.4" paddingY={3}>
                <Text textStyle="textBody">
                  {item.timestamp
                    ? formatDateAndTime(new Date(item.timestamp))
                    : '--'}
                </Text>
              </Table.RowCell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Box>
  </Box>
);

interface EmailDetailsOverlayProps {
  log: IEmailHistoryItem;
  onClose: () => void;
}

const EmailDetailsOverlay = ({ log, onClose }: EmailDetailsOverlayProps) => {
  const { data: trackerData, isFetching } = useQuery({
    queryKey: ['email-tracker', log.trackerId],
    queryFn: () => fetchEmailTrackerData(log.trackerId!),
    enabled: !!log.trackerId
  });

  const activeData = trackerData ?? log;
  
  const recipients = activeData.recipients;
  const subject = activeData.subject ?? '--';
  const body = activeData.body ?? '';
  const manuscriptId = log.manuscriptId;
  const deliveryLogs = trackerData?.webhookEvents ?? [];
  const primaryEmail = recipients?.to?.[0] ?? '--';
  const eventType = deliveryLogs.at(-1)?.eventType ?? ''

  return (
    <Overlay>
      <Flex minHeight="100%" direction="column" backgroundColor="surfaceSecondaryFill">
        <Flex justifyContent="space-between" alignItems="center" p={6}>
          <ButtonText
            variant="secondary"
            onClick={onClose}
            iconLeft={<FontAwesomeIcon icon={faArrowLeft} />}
          >
            Back to All Email Logs
          </ButtonText>
        </Flex>

        <Box flex={1} overflowY="auto" px={{ base: 4, md: 6 }} pb={6}>
          {isFetching ? (
            <SubmissionLoader
              title="Loading Email Details"
              subTitle="We are retrieving the email log details and webhook history."
              bgColor="neutralSecondaryFill"
            />
          ) : (
            <OutlineCard gap={6} maxWidth="980px" mx="auto" p={4}>
              <Flex direction="column" gap={2}>
                <Flex justifyContent="space-between" alignItems="center" gap={4}>
                  <Text textStyle="headingSection">
                    {getEventLabel(eventType ?? '')} - {primaryEmail}
                  </Text>
                </Flex>

                <Flex gap={4} wrap="wrap">
                  {manuscriptId && (
                    <TextWithIcon icon={faHashtag} text={manuscriptId} />
                  )}
                </Flex>
              </Flex>
              
              <Box as="hr" borderColor="borderPrimary" width="100%" mt={2} />
              
              <Box backgroundColor="white" p={6} borderRadius="small">
                <Flex direction="column" gap={5}>
                  <Text textStyle="headingItem">Delivery Details</Text>
                  {deliveryLogs.length > 0 ? (
                    <TrackerDeliveryDetailsTable logs={deliveryLogs} />
                  ) : (
                    <Text textStyle="textBody" color="textSubtle">
                      No webhook delivery events found for this email.
                    </Text>
                  )}
                </Flex>
              </Box>
              
              <Box as="hr" borderColor="borderPrimary" width="100%" />
              
              <Box backgroundColor="white" p={6} borderRadius="small">
                <Flex direction="column" gap={5}>
                  <Text textStyle="headingItem">Email Content</Text>
                  
                  <Flex direction="column" gap={1}>
                    <EmailRecipientRow label="To">
                      {renderRecipient(recipients?.to) || primaryEmail}
                    </EmailRecipientRow>
                    <EmailRecipientRow label="CC">
                      {renderRecipient(recipients?.cc) || '--'}
                    </EmailRecipientRow>
                    <EmailRecipientRow label="BCC">
                      {renderRecipient(recipients?.bcc) || '--'}
                    </EmailRecipientRow>
                  </Flex>
                  
                  <Flex direction="column">
                    <Text textStyle="headingItem">Subject</Text>
                    <Text textStyle="textBody">{subject}</Text>
                  </Flex>

                  <Flex direction="column" gap={2} mt={2}>
                    <Text textStyle="headingItem">Body Text</Text>
                    {body ? (
                      <RichTextPreview value={removeHrefKeepText(body) ?? ''} />
                    ) : (
                      <Text textStyle="textBody">--</Text>
                    )}
                  </Flex>
                </Flex>
              </Box>
            </OutlineCard>
          )}
        </Box>
      </Flex>
    </Overlay>
  );
};

export default EmailDetailsOverlay;